import React, { useEffect, useMemo, useRef, useState } from "react";
import RouteEditor from "./RouteEditor";
import { styled } from "@mui/material/styles";
import { db, storage } from "../firebase";
import {
  collection,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  createTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
  DeleteOutline as DeleteOutlineIcon,
  AddCircleOutline,
  RemoveCircleOutline,
  Map as MapIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";

// Styled Components
const MapContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const MapHeader = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-lg);
  flex-wrap: wrap;
  gap: var(--space-md);
`;

const PageTitle = styled("h1")`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);

  svg {
    width: 40px;
    height: 40px;
    color: var(--accent-primary);
  }
`;

const PageSubtitle = styled("p")`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 400;
`;

const ProductListContainer = styled(Box)`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);

  .MuiList-root {
    background: var(--bg-secondary);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
  }

  .MuiListItemButton-root {
    &:hover {
      background: var(--surface-hover);
    }

    &.Mui-selected {
      background: rgba(16, 185, 129, 0.1);
      border-left: 3px solid var(--accent-primary);
    }
  }
`;

const MapViewContainer = styled(Box)`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  position: relative;
  flex: 1;
  min-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#10b981",
      dark: "#059669",
      light: "#34d399",
    },
    secondary: { main: "#06b6d4" },
    success: { main: "#22c55e" },
    warning: { main: "#f59e0b" },
    error: { main: "#ef4444" },
    info: { main: "#06b6d4" },
    background: { default: "#0a0f0a", paper: "#2d342d" },
    text: { primary: "#f0f9f0", secondary: "#c8d8c8" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 600,
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          },
        },
        contained: {
          background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
          },
        },
        outlined: {
          borderColor: "#10b981",
          color: "#10b981",
          "&:hover": {
            borderColor: "#34d399",
            backgroundColor: "rgba(16,185,129,0.1)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: "16px", fontWeight: 600 },
        colorSuccess: {
          backgroundColor: "rgba(34,197,94,0.2)",
          color: "#22c55e",
        },
        colorDefault: {
          backgroundColor: "rgba(156,163,175,0.2)",
          color: "#9ca3af",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#2d342d",
          backgroundImage: "none",
          border: "1px solid #374151",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#10b981",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#10b981",
            },
          },
        },
      },
    },
  },
});

const toPct = (v) => `${(v * 100).toFixed(2)}%`;

export default function MapEditor({ mapId, onBack }) {
  const [mode, setMode] = useState("pins");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showMissingOnly, setShowMissingOnly] = useState(true);

  const [selected, setSelected] = useState(null);
  const [draftCoord, setDraftCoord] = useState(null);

  const [mapUrl, setMapUrl] = useState("");
  const [mapName, setMapName] = useState("");
  const [mapLoading, setMapLoading] = useState(true);

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);

  const imgRef = useRef(null);
  const boxRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });
  const didDrag = useRef(false);

  useEffect(() => {
    if (zoom === 1) setPanOffset({ x: 0, y: 0 });
  }, [zoom]);

  // Load products
  useEffect(() => {
    const qProducts = query(collection(db, "products"), orderBy("name"));
    const unsub = onSnapshot(
      qProducts,
      (snap) => {
        setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setSnack({ open: true, msg: `Failed to load products: ${err.message}`, severity: "error" });
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // Keyboard shortcut: S → save coordinate
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "s" || e.key === "S") {
        const tag = e.target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        saveCoord();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selected, draftCoord, mapId]);

  // Load map image URL and name from store_maps/{mapId}
  useEffect(() => {
    if (!mapId) {
      setMapLoading(false);
      return;
    }
    let active = true;
    setMapLoading(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, "store_maps", mapId));
        if (active && snap.exists()) {
          setMapUrl(snap.data().image_url || "");
          setMapName(snap.data().name || "");
        }
      } catch (e) {
        if (active) setSnack({ open: true, msg: `Load map failed: ${e.message}`, severity: "error" });
      } finally {
        if (active) setMapLoading(false);
      }
    })();
    return () => (active = false);
  }, [mapId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((p) => {
      const hasCoords = p.map_coords?.[mapId]?.has_coords === true;
      if (showMissingOnly && hasCoords) return false;
      if (!term) return true;
      return (
        (p.name?.toLowerCase() ?? "").includes(term) ||
        (p.barcode?.toLowerCase?.() ?? "").includes(term) ||
        (p.category_name?.toLowerCase?.() ?? "").includes(term)
      );
    });
  }, [products, showMissingOnly, search, mapId]);

  const handleImageClick = (e) => {
    if (didDrag.current) return;
    if (!selected || !imgRef.current) return;
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setDraftCoord({
      x: Math.max(0, Math.min(1, x)),
      y: Math.max(0, Math.min(1, y)),
    });
  };

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    didDrag.current = false;
    dragStart.current = { x: e.clientX - panOffset.x, y: e.clientY - panOffset.y };
    setIsPanning(true);
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;
    e.preventDefault();
    didDrag.current = true;
    setPanOffset({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  };

  const handleMouseUp = () => setIsPanning(false);

  const saveCoord = async () => {
    if (!selected || !draftCoord || !mapId) return;
    try {
      await updateDoc(doc(db, "products", selected.id), {
        [`map_coords.${mapId}`]: {
          x_ratio: Number(draftCoord.x),
          y_ratio: Number(draftCoord.y),
          has_coords: true,
          nearest_node_id: selected.map_coords?.[mapId]?.nearest_node_id ?? null,
        },
        updated_at: new Date(),
      });
      setSnack({
        open: true,
        msg: `Saved ${selected.name} @ (${draftCoord.x.toFixed(4)}, ${draftCoord.y.toFixed(4)})`,
        severity: "success",
      });
      setSelected((s) =>
        s
          ? {
              ...s,
              map_coords: {
                ...(s.map_coords || {}),
                [mapId]: {
                  x_ratio: draftCoord.x,
                  y_ratio: draftCoord.y,
                  has_coords: true,
                  nearest_node_id: s.map_coords?.[mapId]?.nearest_node_id ?? null,
                },
              },
            }
          : s
      );
      setDraftCoord(null);
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: "error" });
    }
  };

  const clearCoord = async () => {
    if (!selected || !mapId) return;
    try {
      await updateDoc(doc(db, "products", selected.id), {
        [`map_coords.${mapId}`]: deleteField(),
        updated_at: new Date(),
      });
      setSnack({ open: true, msg: `Cleared coordinates for ${selected.name}`, severity: "success" });
      setSelected((s) => {
        if (!s) return s;
        const newCoords = { ...(s.map_coords || {}) };
        delete newCoords[mapId];
        return { ...s, map_coords: newCoords };
      });
      setDraftCoord(null);
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: "error" });
    }
  };

  const uploadMap = async (file) => {
    if (!file || !mapId) return;
    try {
      setUploading(true);
      const r = ref(storage, `store_maps/${mapId}/${Date.now()}_${file.name}`);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      await updateDoc(doc(db, "store_maps", mapId), { image_url: url, updated_at: new Date() });
      setMapUrl(url);
      setSnack({ open: true, msg: "Map uploaded", severity: "success" });
      setUploadOpen(false);
    } catch (e) {
      setSnack({ open: true, msg: e.message, severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const removeMap = async () => {
    if (!mapId) return;
    await updateDoc(doc(db, "store_maps", mapId), { image_url: "" });
    setMapUrl("");
  };

  // Marker: draft position takes priority over saved coords for selected product
  const savedCoords = selected?.map_coords?.[mapId];
  const marker =
    draftCoord ||
    (savedCoords?.x_ratio != null ? { x: savedCoords.x_ratio, y: savedCoords.y_ratio } : null);

  return (
    <>
      <MapContainer>
        <MapHeader>
          <Stack direction="row" spacing={1} alignItems="center">
            {onBack && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{ mr: 1, color: "text.secondary" }}
              >
                All Maps
              </Button>
            )}
            <div>
              <PageTitle>
                <MapIcon />
                {mapName || "Map Editor"}
              </PageTitle>
              <PageSubtitle>
                {mode === "pins"
                  ? "Click on the map to place a pin for the selected product."
                  : "Build walkable route nodes and edges for indoor wayfinding."}
              </PageSubtitle>
            </div>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              size="small"
            >
              <ToggleButton value="pins">Product Pins</ToggleButton>
              <ToggleButton value="route">Route Editor</ToggleButton>
            </ToggleButtonGroup>
            {mode === "pins" && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setUploadOpen(true)}
                >
                  Upload / Change Map
                </Button>
                {mapUrl && (
                  <Tooltip title="Remove map image (keeps file in Storage)">
                    <span>
                      <IconButton onClick={removeMap}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>
        </MapHeader>

        {mode === "route" && <RouteEditor mapUrl={mapUrl} mapId={mapId} />}

        {mode === "pins" && (
          <>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              {/* LEFT: product list */}
              <ProductListContainer sx={{ width: { xs: "100%", md: 360 }, flexShrink: 0 }}>
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    placeholder="Search product name / barcode / category"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Switch
                      checked={showMissingOnly}
                      onChange={(e) => setShowMissingOnly(e.target.checked)}
                    />
                    <Typography variant="body2">
                      Show only products missing coordinates
                    </Typography>
                    <Chip size="small" label={filtered.length} sx={{ ml: "auto" }} />
                  </Stack>
                  <Divider />
                  {loading ? (
                    <LinearProgress />
                  ) : (
                    <List
                      dense
                      sx={{
                        maxHeight: 560,
                        overflow: "auto",
                        border: "1px solid #eee",
                        borderRadius: 1,
                      }}
                    >
                      {filtered.map((p) => {
                        const hasCoords = p.map_coords?.[mapId]?.has_coords === true;
                        return (
                          <ListItem
                            key={p.id}
                            disablePadding
                            secondaryAction={
                              hasCoords ? (
                                <Chip size="small" color="success" label="Set" />
                              ) : (
                                <Chip size="small" label="Missing" />
                              )
                            }
                          >
                            <ListItemButton
                              selected={selected?.id === p.id}
                              onClick={() => {
                                setSelected(p);
                                setDraftCoord(null);
                              }}
                            >
                              <ListItemText
                                primary={p.name || "(no name)"}
                                secondary={
                                  (p.barcode ? `#${p.barcode}` : "") +
                                  (p.category_name ? ` • ${p.category_name}` : "")
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                      {filtered.length === 0 && (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No products match your filter.
                          </Typography>
                        </Box>
                      )}
                    </List>
                  )}
                </Stack>
              </ProductListContainer>

              {/* RIGHT: map + pin */}
              <MapViewContainer
                ref={boxRef}
                sx={{ cursor: isPanning ? "grabbing" : zoom > 1 ? "grab" : "default" }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {mapLoading ? (
                  <LinearProgress sx={{ width: "100%" }} />
                ) : mapUrl ? (
                  <>
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        transform: `scale(${zoom}) translate(${panOffset.x}px, ${panOffset.y}px)`,
                        transition: isPanning ? "none" : "transform 0.2s ease-out",
                      }}
                    >
                      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                        <img
                          ref={imgRef}
                          src={mapUrl}
                          alt="Store map"
                          onClick={handleImageClick}
                          onDragStart={(e) => e.preventDefault()}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            display: "block",
                            cursor: selected ? "crosshair" : "inherit",
                          }}
                        />
                        {marker && (
                          <Box
                            sx={{
                              position: "absolute",
                              left: `${marker.x * 100}%`,
                              top: `${marker.y * 100}%`,
                              transform: "translate(-50%, -50%)",
                              pointerEvents: "none",
                              zIndex: 10,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#e53935",
                                boxShadow: "0 0 0 3px rgba(229,57,53,.25)",
                                border: "2px solid white",
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Footer overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(255,255,255,.9)",
                        borderTop: "1px solid #eee",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        pointerEvents: "none",
                      }}
                    >
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {selected
                          ? `Selected: ${selected.name}`
                          : "Select a product to place a pin"}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        {marker
                          ? `x=${marker.x.toFixed(4)} • y=${marker.y.toFixed(4)}`
                          : "x=— • y=—"}
                      </Typography>
                    </Box>

                    {/* Zoom controls */}
                    <Stack spacing={1} sx={{ position: "absolute", top: 8, right: 8 }}>
                      <IconButton onClick={() => setZoom((z) => Math.min(z + 0.2, 5))}>
                        <AddCircleOutline />
                      </IconButton>
                      <IconButton onClick={() => setZoom((z) => Math.max(z - 0.2, 1))}>
                        <RemoveCircleOutline />
                      </IconButton>
                    </Stack>
                  </>
                ) : (
                  <Stack spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      No map uploaded yet.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setUploadOpen(true)}
                    >
                      Upload Map
                    </Button>
                  </Stack>
                )}
              </MapViewContainer>
            </Stack>

            {/* Actions */}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                disabled={!selected || !selected.map_coords?.[mapId]?.has_coords}
                onClick={clearCoord}
                color="warning"
              >
                Clear coordinate
              </Button>
              <Tooltip title="Press S to save quickly">
                <span>
                  <Button
                    variant="contained"
                    disabled={!selected || !draftCoord}
                    onClick={saveCoord}
                  >
                    Save coordinate [S]
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </>
        )}
      </MapContainer>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)}>
        <DialogTitle>Upload Store Map</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Supported images (PNG/JPG). The image is stored in Firebase Storage
            under <code>store_maps/{mapId}/</code>.
          </Typography>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={uploading}
          >
            {uploading ? "Uploading…" : "Choose file"}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => uploadMap(e.target.files?.[0])}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
